import { XMLBuilder } from 'fast-xml-parser';
import TimeStamp from '../utils/TimeStamp';
import { getMainSellerWithAddress } from '../utils/Database';
import dataService from './DataService';

/**
 * Service pour la génération de documents au format Factur-X (facture électronique)
 * Permet de créer des documents XML conformes à la norme européenne EN16931
 */
export default class FacturXService {
  /**
   * Initialise le service Factur-X avec les options de configuration XML
   */
  constructor() {
    this.options = {
      ignoreAttributes: false,
      format: true,
      attributeNamePrefix: "@_",
      suppressEmptyNode: true
    };
  }
  
  /**
   * Génère un document XML minimal au format Factur-X
   * @param {Object} bill - Objet facture avec les informations principales
   * @param {Object} billData - Données détaillées de la facture (optionnel)
   * @returns {Promise<string>} Document XML formaté selon le profil Minimal
   */
  async generateMinimalXML(bill, billData) {
    // Récupération sécurisée des montants - même logique que generateXML
    const getNumber = (val, fallback = 0) => {
      const n = Number(val);
      return isNaN(n) ? fallback : n;
    };
    
    // Récupérer les informations du vendeur principal
    let sellerInfo;
    try {
      sellerInfo = await getMainSellerWithAddress();
    } catch (error) {
      console.error("Erreur lors de la récupération des informations du vendeur:", error);
      sellerInfo = { account: {}, address: null };
    }
    
    // Récupérer les informations du client et du devis
    let quote = null;
    let clientAccount = null;
    // Variables non utilisées car pour le profil minimal, seul CountryID est requis
    // let billingAddress = null;
    // let billingContact = null;
    
    if (bill.quoteId) {
      try {
        // Récupérer le devis complet
        quote = await dataService.getQuoteWithLines(bill.quoteId);
        
        if (quote && quote.opportunityId) {
          // Récupérer l'opportunité pour avoir l'ID du compte client
          const opportunity = await dataService.getById('opportunities', quote.opportunityId);
          
          if (opportunity && opportunity.accountId) {
            // Récupérer les détails du compte client
            clientAccount = await dataService.getAccountWithDetails(opportunity.accountId);
            
            // Ces récupérations ne sont plus nécessaires pour le profil minimal
            // Nous ne les conservons que si nous prévoyons d'évoluer vers des profils plus avancés plus tard
            /* 
            // Récupérer l'adresse de facturation
            billingAddress = clientAccount?.addresses?.find(addr => addr.isBillingLocation) || 
                             clientAccount?.addresses?.[0] || null;
                             
            // Récupérer le contact de facturation
            billingContact = clientAccount?.contacts?.find(contact => contact.isBillingContact) ||
                             clientAccount?.contacts?.[0] || null;
            */
          }
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des informations du client:", error);
      }
    }
    
    // Utiliser le montant facturé (facture partielle ou totale)
    let netAmount = getNumber(bill?.currentProgressAmount || bill?.netAmount, 0);
    let vatRate = 20;
    
    // Déterminer le taux de TVA à partir des lignes ou du taux global
    if (Array.isArray(bill.lines) && bill.lines.length > 0) {
      const firstLine = bill.lines.find(l => l.parentLineId === null || l.parentLineId === undefined);
      if (firstLine && firstLine.lineVatRate && firstLine.lineVatRate.data) {
        vatRate = getNumber(firstLine.lineVatRate.data, 20);
      }
    } else if (bill.vatRate && bill.vatRate.data) {
      vatRate = getNumber(bill.vatRate.data, 20);
    } else if (bill.vatRate) {
      vatRate = getNumber(bill.vatRate, 20);
    }

    // Calculs des montants avec arrondis
    const vatAmount = Math.round((netAmount * vatRate / 100) * 100) / 100;
    const totalAmount = Math.round((netAmount + vatAmount) * 100) / 100;
    
    // Extraire le SIREN du numéro de TVA si disponible
    // Le SIREN correspond aux 9 derniers chiffres du numéro de TVA français
    const sellerVatId = bill.sellerTaxId || sellerInfo.account.taxId || '';
    let sellerSiren = '';
    
    // Méthode 1: Si format standard FR12345678901, prendre les 9 derniers chiffres
    const sirenMatch = sellerVatId.match(/(\d{9})$/);
    if (sirenMatch) {
      sellerSiren = sirenMatch[1];
    } 
    // Méthode 2: Sinon, extraire tous les chiffres et prendre les 9 derniers
    else {
      const allDigits = sellerVatId.replace(/\D/g, '');
      sellerSiren = allDigits.slice(-9).padStart(9, '0');
    }
    
    // Simplification de l'objet adresse du vendeur - seul CountryID est requis pour le profil minimal
    const sellerAddress = {
      "ram:CountryID": "FR"
    };
    
    // Construction de l'objet vendeur avec les informations de la base de données
    const sellerParty = {
      "ram:Name": bill.sellerName || sellerInfo.account.name || "",
      "ram:SpecifiedLegalOrganization": {
        "ram:ID": {
          "@_schemeID": "0002",
          "#text": sellerSiren
        }
      },
      "ram:PostalTradeAddress": sellerAddress
    };
    
    // Ajouter SpecifiedTaxRegistration pour le vendeur si disponible
    if (sellerVatId) {
      sellerParty["ram:SpecifiedTaxRegistration"] = {
        "ram:ID": {
          "@_schemeID": "VA",
          "#text": sellerVatId
        }
      };
    }
    
    // Simplification de l'objet adresse du client - seul CountryID est requis pour le profil minimal
    const buyerAddress = {
      "ram:CountryID": "FR"
    };
    
    // Construction de l'objet client en utilisant les informations récupérées
    const buyerParty = {
      "ram:Name": bill.clientName || clientAccount?.name || "",
      "ram:PostalTradeAddress": buyerAddress
    };
    
    // Ajouter SpecifiedTaxRegistration pour le client si disponible
    const clientTaxId = billData?.customerTaxID || bill.customerTaxID || clientAccount?.taxId;
    if (clientTaxId) {
      buyerParty["ram:SpecifiedTaxRegistration"] = {
        "ram:ID": {
          "@_schemeID": "VA",
          "#text": clientTaxId
        }
      };
    }
    
    // Structure minimale requise pour Factur-X Minimal avec préfixes d'espace de noms
    const invoice = {
      "rsm:CrossIndustryInvoice": {
        "@_xmlns:qdt": "urn:un:unece:uncefact:data:standard:QualifiedDataType:100",
        "@_xmlns:ram": "urn:un:unece:uncefact:data:standard:ReusableAggregateBusinessInformationEntity:100",
        "@_xmlns:rsm": "urn:un:unece:uncefact:data:standard:CrossIndustryInvoice:100",
        "@_xmlns:udt": "urn:un:unece:uncefact:data:standard:UnqualifiedDataType:100",
        "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
        "rsm:ExchangedDocumentContext": {
          "ram:GuidelineSpecifiedDocumentContextParameter": {
            "ram:ID": "urn:factur-x.eu:1p0:minimum"
          }
        },
        "rsm:ExchangedDocument": {
          "ram:ID": bill.billCustomId || "",
          "ram:TypeCode": bill.invoiceTypeCode || "380",
          "ram:IssueDateTime": {
            "udt:DateTimeString": {
              "@_format": "102",
              "#text": this.formatDate(bill.timeStamp)
            }
          }
        },
        "rsm:SupplyChainTradeTransaction": {
          "ram:ApplicableHeaderTradeAgreement": {
            "ram:SellerTradeParty": sellerParty,
            "ram:BuyerTradeParty": buyerParty
          },
          "ram:ApplicableHeaderTradeDelivery": {},
          "ram:ApplicableHeaderTradeSettlement": {
            "ram:InvoiceCurrencyCode": bill.currencyCode || billData?.currencyCode || "EUR",
            "ram:SpecifiedTradeSettlementHeaderMonetarySummation": {
              "ram:TaxBasisTotalAmount": netAmount.toFixed(2),
              "ram:TaxTotalAmount": {
                "@_currencyID": bill.currencyCode || billData?.currencyCode || "EUR",
                "#text": vatAmount.toFixed(2)
              },
              "ram:GrandTotalAmount": totalAmount.toFixed(2),
              "ram:DuePayableAmount": totalAmount.toFixed(2)
            }
          }
        }
      }
    };
    
    // Convertir en XML
    const builder = new XMLBuilder(this.options);
    const xmlContent = builder.build(invoice);
    
    // Ajouter la déclaration XML au début du document
    return `<?xml version='1.0' encoding='UTF-8'?>\n${xmlContent}`;
  }

  /**
   * Point d'entrée pour générer un document XML au format Factur-X
   * Cette méthode appelle generateMinimalXML pour générer le XML au profil minimal
   * @param {Object} bill - Objet facture avec les informations principales
   * @param {Object} billData - Données détaillées de la facture (optionnel)
   * @returns {Promise<string>} Document XML formaté
   */
  async generateXML(bill, billData) {
    // Utiliser le profil minimal par défaut
    return this.generateMinimalXML(bill, billData);
  }
  
  /**
   * Génère un PDF conforme à la norme Factur-X en combinant le XML et le PDF visuel
   * @param {Object} bill - Données de facturation
   * @param {Buffer} visualPDF - PDF visuel de la facture
   * @returns {Promise<Buffer>} PDF hybride conforme à Factur-X
   */
  async generateFacturXPDF(bill, visualPDF) {
    // Cette méthode nécessite une dépendance comme 'pdf-lib' ou similaire
    // Principe : injecter le XML généré comme pièce jointe au PDF visuel
    
    // Générer le XML
    // const xmlContent = await this.generateMinimalXML(bill);
    
    // Code pour injecter le XML dans le PDF
    // Note: cette partie nécessiterait l'implémentation d'une bibliothèque de manipulation PDF
    
    return null; // À implémenter avec la bibliothèque appropriée
  }
  
  /**
   * Extrait et analyse le contenu XML d'une facture Factur-X
   * @param {Buffer} pdfData - Données binaires du PDF Factur-X
   * @returns {Promise<Object>} Données structurées de la facture
   */
  async extractFacturXData(pdfData) {
    // Cette méthode nécessite une dépendance pour extraire le XML du PDF
    // Principe : extraire la pièce jointe XML et la parser
    
    // Code pour extraire le XML du PDF
    
    return {
      // Structure des données extraites
      billNumber: "",
      date: "",
      parties: {
        seller: {},
        buyer: {}
      },
      lines: [],
      amounts: {}
    };
  }
  
  /**
   * Formate une date au format AAAAMMJJ requis par la norme Factur-X
   * @param {Object} timestamp - Objet timestamp contenant la date
   * @param {number} timestamp.year - Année
   * @param {number} timestamp.month - Mois
   * @param {number} timestamp.day - Jour
   * @returns {string} Date formatée (AAAAMMJJ)
   */
  formatDate(timestamp) {
    if (!timestamp) return '';
    return `${timestamp.year}${String(timestamp.month).padStart(2, '0')}${String(timestamp.day).padStart(2, '0')}`;
  }
  
  /**
   * Ajoute les informations sur le vendeur et l'acheteur au document
   * @param {Object} invoice - Structure du document Factur-X
   * @param {Object} billData - Données de la facture
   */
  addPartyInfo(invoice, billData) {
    // Structure pour les informations sur les parties (vendeur et acheteur)
    const partyInfo = {
      "SellerTradeParty": {
        "ID": billData.sellerTaxID || "",
        "Name": billData.clientName || "Entreprise",
        "SpecifiedLegalOrganization": {
          "ID": {
            "@_schemeID": "0002",
            "#text": billData.sellerTaxID || ""
          }
        },
        "PostalTradeAddress": this.formatAddress(billData.sellerAddress),
        "SpecifiedTaxRegistration": {
          "ID": {
            "@_schemeID": "VA",
            "#text": billData.sellerTaxID || ""
          }
        }
      },
      "BuyerTradeParty": {
        "Name": billData.clientName || "",
        "PostalTradeAddress": this.formatAddress(billData.clientAddress?.line1, billData.clientAddress?.line2),
        "SpecifiedTaxRegistration": {
          "ID": {
            "@_schemeID": "VA",
            "#text": billData.customerTaxID || ""
          }
        }
      }
    };
    
    // Ajouter les parties à la structure principale
    invoice.CrossIndustryInvoice.SupplyChainTradeTransaction = {
      ...invoice.CrossIndustryInvoice.SupplyChainTradeTransaction,
      "ApplicableHeaderTradeAgreement": {
        "BuyerReference": billData.refClient || "",
        "SellerTradeParty": partyInfo.SellerTradeParty,
        "BuyerTradeParty": partyInfo.BuyerTradeParty,
        "BuyerOrderReferencedDocument": {
          "IssuerAssignedID": billData.command || ""
        },
        "ContractReferencedDocument": {
          "IssuerAssignedID": billData.quoteNumber || ""
        }
      }
    };
  }
  
  /**
   * Ajoute les lignes de facture au document
   * @param {Object} invoice - Structure du document Factur-X
   * @param {Object} billData - Données de la facture contenant les lignes
   */
  addLineItems(invoice, billData) {
    if (!billData.items || !Array.isArray(billData.items)) {
      // Code existant pour le cas sans items
      return;
    }
    
    // Traitement des lignes existantes
    const lines = billData.items.map((item, index) => {
      const lineAmount = (item.unitPrice || 0) * (item.quantity || 1);
      return {
        "AssociatedDocumentLineDocument": {
          "LineID": (index + 1).toString()
        },
        "SpecifiedTradeProduct": {
          "Name": item.description || "Produit/Service",
          "ID": item.reference || ""
        },
        "SpecifiedLineTradeAgreement": {
          "NetPriceProductTradePrice": {
            "ChargeAmount": item.unitPrice?.toFixed(2) || "0.00"
          }
        },
        "SpecifiedLineTradeDelivery": {
          "BilledQuantity": {
            "@_unitCode": item.unit || "EA",
            "#text": item.quantity?.toString() || "1"
          }
        },
        "SpecifiedLineTradeSettlement": {
          "ApplicableTradeTax": {
            "TypeCode": "VAT",
            "CategoryCode": "S",
            "RateApplicablePercent": item.vatRate?.toFixed(2) || billData.vatRate?.toFixed(2) || "20.00"
          },
          "SpecifiedTradeSettlementLineMonetarySummation": {
            "LineTotalAmount": lineAmount.toFixed(2)
          }
        }
      };
    });
    
    invoice.CrossIndustryInvoice.SupplyChainTradeTransaction = {
      ...invoice.CrossIndustryInvoice.SupplyChainTradeTransaction,
      "IncludedSupplyChainTradeLineItem": lines
    };
  }
  
  /**
   * Ajoute les informations de montants totaux au document
   * @param {Object} invoice - Structure du document Factur-X
   * @param {Object} billData - Données de la facture avec les montants
   */
  addTotals(invoice, billData) {
    // Calculer les montants à partir de l'objet bill
    const netAmount = this.calculateNetAmount(billData);
    const vatAmount = this.calculateVatAmount(billData, netAmount);
    const totalWithTax = netAmount + vatAmount;
    
    // S'assurer que les montants sont cohérents
    const monetarySummation = {
      "LineTotalAmount": netAmount.toFixed(2),
      "TaxBasisTotalAmount": netAmount.toFixed(2),
      "TaxTotalAmount": {
        "@_currencyID": billData.currencyCode || "EUR",
        "#text": vatAmount.toFixed(2)
      },
      "GrandTotalAmount": totalWithTax.toFixed(2),
      "DuePayableAmount": totalWithTax.toFixed(2) // Même montant que le total
    };
    
    // Période de facturation (facultatif)
    const billingPeriod = billData.date ? {
      "StartDateTime": {
        "DateTimeString": {
          "@_format": "102",
          "#text": this.formatDate(TimeStamp.fromString(billData.date))
        }
      },
      "EndDateTime": {
        "DateTimeString": {
          "@_format": "102",
          "#text": this.formatDate(TimeStamp.fromString(billData.date))
        }
      }
    } : null;
    
    // Informations sur la TVA
    const taxTotal = {
      "TaxSubtotal": {
        "TaxableAmount": netAmount.toFixed(2),
        "TaxAmount": vatAmount.toFixed(2),
        "TaxCategory": {
          "ID": "S",
          "Percent": billData.vatRate?.toFixed(2) || "20.00"
        }
      }
    };
    
    // Ajouter les totaux à la structure principale
    invoice.CrossIndustryInvoice.SupplyChainTradeTransaction = {
      ...invoice.CrossIndustryInvoice.SupplyChainTradeTransaction,
      "ApplicableHeaderTradeSettlement": {
        ...(invoice.CrossIndustryInvoice.SupplyChainTradeTransaction.ApplicableHeaderTradeSettlement || {}),
        "ApplicableTradeTax": [{
          "CalculatedAmount": vatAmount.toFixed(2),
          "TypeCode": "VAT",
          "BasisAmount": netAmount.toFixed(2),
          "CategoryCode": "S",
          "RateApplicablePercent": billData.vatRate?.toFixed(2) || "20.00"
        }],
        "SpecifiedTradeSettlementHeaderMonetarySummation": monetarySummation,
        "ApplicableTradeTaxSummation": taxTotal // Intégrer taxTotal dans la structure
      },
      "ApplicableHeaderTradeDelivery": billingPeriod || {}
    };
  }

  /**
   * Calcule le montant net à partir des lignes de facture
   * @param {Object} billData - Données de la facture
   * @returns {number} Montant net calculé
   */
  calculateNetAmount(billData) {
    if (!billData.items || !Array.isArray(billData.items)) {
      return billData.netAmount || 0;
    }
    
    // Calculer le total à partir des lignes
    return billData.items.reduce((total, item) => {
      const lineAmount = (item.unitPrice || 0) * (item.quantity || 1);
      return total + lineAmount;
    }, 0);
  }

  /**
   * Calcule le montant de TVA avec gestion des arrondis selon les règles fiscales
   * @param {Object} billData - Données de la facture
   * @param {number} netAmount - Montant net
   * @returns {number} Montant de TVA calculé et arrondi
   */
  calculateVatAmount(billData, netAmount) {
    const vatRate = billData.vatRate || 20;
    // Arrondir à 2 décimales selon les règles fiscales françaises
    return Math.round((netAmount * vatRate / 100) * 100) / 100;
  }
  
  /**
   * Ajoute les informations de paiement au document
   * @param {Object} invoice - Structure du document Factur-X
   * @param {Object} bill - Informations principales de la facture
   * @param {Object} billData - Données détaillées de la facture
   */
  addPaymentInfo(invoice, bill, billData) {
    // Informations de paiement
    const paymentMeans = {
      "TypeCode": this.getPaymentTypeCode(bill.paymentMethod || "CREDIT_TRANSFER"),
      "Information": bill.paymentTerms || "30 jours fin de mois",
      "PayeePartyCreditorFinancialAccount": {
        "IBANID": billData.ibanAccount || ""
      }
    };
    
    // Date d'échéance
    const dueDate = bill.paymentDueDate ? {
      "DueDateDateTime": {
        "DateTimeString": {
          "@_format": "102",
          "#text": this.formatDate(bill.paymentDueDate)
        }
      }
    } : {};
    
    // Ajouter les informations de paiement à la structure principale
    if (!invoice.CrossIndustryInvoice.SupplyChainTradeTransaction.ApplicableHeaderTradeSettlement) {
      invoice.CrossIndustryInvoice.SupplyChainTradeTransaction.ApplicableHeaderTradeSettlement = {};
    }
    
    invoice.CrossIndustryInvoice.SupplyChainTradeTransaction.ApplicableHeaderTradeSettlement = {
      ...invoice.CrossIndustryInvoice.SupplyChainTradeTransaction.ApplicableHeaderTradeSettlement,
      "PaymentReference": bill.paymentReference || bill.billCustomId || "",
      "InvoiceCurrencyCode": bill.currencyCode || "EUR",
      "SpecifiedTradePaymentTerms": dueDate,
      "SpecifiedTradeSettlementPaymentMeans": paymentMeans
    };
  }
  
  /**
   * Formatte une adresse pour le format Factur-X
   * @param {string} line1 - Première ligne d'adresse (rue, numéro)
   * @param {string} line2 - Seconde ligne d'adresse (code postal et ville)
   * @returns {Object} Adresse formatée pour Factur-X
   */
  formatAddress(line1, line2) {
    // Pour le profil minimal, seul CountryID est requis
    return {
      "CountryID": "FR"
    };
  }
  
  /**
   * Détermine le code de type de paiement selon la norme Factur-X
   * @param {string} paymentMethod - Méthode de paiement utilisée
   * @returns {string} Code du type de paiement
   */
  getPaymentTypeCode(paymentMethod) {
    switch (paymentMethod.toUpperCase()) {
      case 'CREDIT_TRANSFER':
        return '30'; // Virement
      case 'DIRECT_DEBIT':
        return '49'; // Prélèvement
      case 'CARD':
        return '48'; // Carte bancaire
      case 'CHEQUE':
        return '20'; // Chèque
      case 'CASH':
        return '10'; // Espèces
      default:
        return '30'; // Par défaut: virement
    }
  }

  /**
   * Vérifie que les informations minimales requises sont présentes
   * @param {Object} bill - Objet facture
   * @returns {Object} Résultat de validation {isValid: boolean, errors: string[]}
   */
  validateMinimalRequirements(bill) {
    const errors = [];
    
    if (!bill.billCustomId) errors.push("Numéro de facture manquant");
    if (!bill.timeStamp) errors.push("Date de facture manquante");
    if (!bill.sellerName) errors.push("Nom du vendeur manquant");
    if (!bill.clientName) errors.push("Nom du client manquant");
    if (!bill.netAmount && bill.netAmount !== 0) errors.push("Montant net manquant");
    
    // Vérification de la cohérence des montants
    const netAmount = bill.netAmount || 0;
    const vatRate = bill.vatRate || 20;
    const calculatedVat = netAmount * (vatRate / 100);
    const calculatedTotal = netAmount + calculatedVat;
    
    if (bill.totalAmount && Math.abs(bill.totalAmount - calculatedTotal) > 0.01) {
      errors.push("Incohérence entre montant total et montant calculé");
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
}